import Skeleton from '../../shared/Skeleton'

/** Placeholder for a `ProductCard` row (prototype geometry): the 130×156
 * image tile fixes the row height, so the swap causes no layout shift. */
function ProductCardSkeleton() {
  return (
    <div className="flex gap-16 w-full">
      <Skeleton w={130} h={156} className="shrink-0" />
      <div className="flex flex-1 flex-col justify-between pb-2">
        <div className="flex flex-col gap-10">
          <Skeleton h={16} className="w-[66%]" />
          <Skeleton h={12} className="w-[30%]" />
          <Skeleton h={12} className="w-[22%]" />
        </div>
        <div className="flex items-end justify-between">
          <Skeleton w={56} h={12} />
          <Skeleton w={72} h={16} />
        </div>
      </div>
    </div>
  )
}

/**
 * Exact-layout placeholder for `OrdersTabView` (RP-5936 prototype geometry):
 * breadcrumbs, order header, two item rows, the price-breakdown rows and the
 * purchase-details card, in the view's own container so the swap to content
 * causes no layout shift. (FAQ/manage-order sections are not skeletonized —
 * per the prototype they appear with the content.)
 */
export default function OrderDetailsSkeleton() {
  return (
    <div
      className="flex flex-col px-16 pb-80 md:pb-120 md:max-lg:mx-auto md:max-w-[522px] lg:px-0 3xl:max-w-[574px]"
      aria-busy="true"
      data-testid="order-details-skeleton"
    >
      {/* Navigation: mobile back arrow / desktop breadcrumb bars */}
      <div className="flex items-start max-lg:pt-32 pb-16">
        <div className="md:hidden flex items-center">
          <Skeleton w={20} h={20} r={99999} />
        </div>
        <div className="hidden md:flex h-20 items-center gap-8">
          <Skeleton w={44} h={12} />
          <Skeleton w={84} h={12} />
        </div>
      </div>

      <div className="flex flex-col gap-48">
        {/* Order header: order number + subtitle */}
        <div className="flex flex-col gap-8">
          <Skeleton h={28} className="w-[62%]" />
          <Skeleton h={16} className="w-[40%]" />
        </div>

        <div className="flex flex-col gap-16">
          {/* Order items heading + two rows */}
          <Skeleton w={140} h={24} className="my-4" />
          <div className="flex flex-col gap-20">
            <ProductCardSkeleton />
            <ProductCardSkeleton />
          </div>

          {/* Price breakdown rows */}
          <div className="flex flex-col gap-10 pt-8">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="flex items-center justify-between">
                <Skeleton w={88} h={12} />
                <Skeleton w={64} h={12} />
              </div>
            ))}
          </div>
        </div>

        {/* Purchase details: heading + bordered card with three row pairs */}
        <div className="flex flex-col gap-16">
          <Skeleton w={186} h={24} className="my-4" />
          <div className="overflow-hidden rounded-lg border border-border-tertiary px-[19px] bg-surface">
            {Array.from({ length: 3 }, (_, index) => (
              <div
                key={index}
                className="flex flex-col gap-10 border-b border-border-tertiary pt-[19px] pb-24 last:border-b-0"
              >
                <Skeleton w={150} h={16} />
                <Skeleton h={12} className="w-[60%]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
